import { RenderableData, Drive } from "./fetchFiles";
import { sortFileTree } from "./sortorder";
import type { ReadOnlyContext } from "./specialFolders";

/**
 * How long the render path leaves a failed load alone before trying it again
 * (#11724). Long enough that a re-render storm cannot turn into a request
 * storm, short enough that a tree which failed during a blip repopulates on
 * its own rather than waiting for a remount. Requests already retry underneath
 * this — fetchFiles goes through react-query's default three tries with
 * backoff — so reaching here at all means several seconds of failure.
 */
const RETRY_AFTER_FAILURE_MS = 5000;

/**
 * Payload type for Datum objects.
 * Extends RenderableData (API types) with optional frontend-only fields.
 */
export type DatumPayload =
  | (RenderableData & { type: "file" })
  | (RenderableData & {
      type: "folder";
      /** Display name for synthetic drive folders (path uses driveId for uniqueness) */
      driveName?: string;
    });

/**
 * A filesystem tree. Uses a flat list of nodes as source of truth,
 * rebuilding parent/children links on every change.
 */
export class Filetree {
  public root = new Datum([], "folder");
  public datums: Datum[] = [this.root];
  public onRebuild: undefined | ((cause: Datum) => void) = undefined;

  // Track datums pending a move (for UI styling)
  public pendingMoveDatums: Set<Datum> = new Set();

  // Whether in across-drives mode (first path segment is synthetic drive name)
  public isAcrossDrivesMode: boolean = false;

  // Names of this org's read-only client drives, and the SFTP-backed subset,
  // as served on the drives listing (#10972).
  public readOnlyDriveNames: string[] = [];
  public sftpDriveNames: string[] = [];
  // Named SharePoint site roots, served per-org (#11713).
  public sharepointRootNames: string[] = [];

  // Drives information (populated when root is loaded)
  public drives: Drive[] = [];
  public drivesEnabled: boolean = false;

  /**
   * Whether paths in this filetree have a synthetic drive prefix that should be stripped
   * before sending to APIs. This is true ONLY when:
   * 1. We're in across-drives mode (viewing multiple drives)
   * 2. AND drives are actually enabled for this organization
   */
  get shouldStripDrivePrefix(): boolean {
    return this.isAcrossDrivesMode && this.drivesEnabled;
  }

  // Sort settings
  public sortBy: "name" | "date" = "name";
  public sortDirection: "asc" | "desc" = "asc";

  // Version counter - increments when folder IDs change (rename/move)
  // Used to force tree component remount
  public version: number = 0;

  // Lookup initialized after sort settings
  public lookup: Map<string, Datum> = rebuild(
    this.datums,
    this.sortBy,
    this.sortDirection,
    {
      readOnlyDriveNames: this.readOnlyDriveNames,
      sftpDriveNames: this.sftpDriveNames,
      sharepointRootNames: this.sharepointRootNames,
      isAcrossDrivesMode: this.isAcrossDrivesMode,
    }
  );

  // External state (survives datum replacement in splice)
  private loadedAt: Map<string, Date> = new Map();
  private reloadingIds: Set<string> = new Set();
  /**
   * When each datum's last load threw, for the back-off in contents().
   * Cleared by a successful load, by invalidate(), and for descendants that a
   * successful splice replaced.
   */
  private failedAt: Map<string, number> = new Map();

  /**
   * Updates the websocket applied while a load was in flight, in arrival
   * order, each with a sequence number. A listing answer was read before
   * those updates happened, so splicing it in would put back the older state
   * (a finished file greyed again, a pushed file gone) and no further frame
   * comes to fix it (#12187). load() replays the ones newer than its own
   * start after its splice. Kept only while a load is out, so the log is
   * bounded by what arrives during one, and owned by this tree.
   */
  private pushedDuringLoad: { seq: number; reapply: () => void }[] = [];
  private pushSeq = 0;

  /**
   * Apply a pushed (websocket) update now, and keep it for replay over any
   * load that is in flight. `apply` must be safe to run again: it merges the
   * update into whatever the tree holds at the time.
   */
  applyPushedUpdate(apply: () => void): void {
    apply();
    this.pushSeq += 1;
    if (this.reloadingIds.size > 0) {
      this.pushedDuringLoad.push({ seq: this.pushSeq, reapply: apply });
    }
  }

  /** Check if a datum or any ancestor is currently loading */
  isReloading(datum: Datum): boolean {
    if (this.reloadingIds.has(datum.id)) return true;
    if (datum.parent) return this.isReloading(datum.parent);
    return false;
  }

  /** Check if a datum has been loaded */
  isLoaded(datum: Datum): boolean {
    return this.loadedAt.has(datum.id);
  }

  /**
   * Check if a folder is known to be empty.
   * A folder is known empty only once its OWN load returned nothing.
   *
   * Emptiness cannot be inferred from the parent's load: loads fetch folder
   * structure one level deeper than they render but file rows only at the
   * rendered level (#11322), so a folder full of files arrives from its
   * parent's load with no children datums at all. Inferring emptiness from
   * the parent rendered such folders as blank — no loading spinner — while
   * their contents fetched. The cost of the stricter rule is a brief spinner
   * on first expand of a truly empty folder.
   */
  isKnownEmpty(datum: Datum): boolean {
    if (datum.type !== "folder") return false;
    if (datum.children && datum.children.length > 0) return false;
    return this.isLoaded(datum);
  }

  /**
   * Synchronously returns the current state of a datum's children.
   * Triggers an async load if the datum hasn't been loaded yet.
   */
  contents(datum: Datum, loader: LoaderFn): DatumContents {
    const isStale = !this.loadedAt.has(datum.id);
    // A load that just failed is left alone for a moment before being tried
    // again. Every render calls this, so retrying a failure immediately is not
    // "one more attempt" — it is a loop clocked at the render rate where every
    // turn throws. #11724 logged 805,860 of those in one CI shard, taking the
    // wall clock to 2150s against 170s of real work until the runner killed
    // the job.
    //
    // Backing off rather than latching the failure permanently is deliberate.
    // A tree that never retries recovers only on remount: there is no error
    // state and no spinner for a failed load (TreeLoadingWrapper keys off
    // isReloading), so a folder whose load failed renders as *empty*, and a
    // latched one would keep claiming that after the outage ended. The window
    // keeps the request count proportional to elapsed time instead of to
    // renders, while still letting a transient failure heal.
    //
    // Note this retries on the next RENDER that happens at least
    // RETRY_AFTER_FAILURE_MS after the failure — no timer is scheduled here,
    // and the onRebuild the failure does schedule fires immediately, inside
    // the window. Something normally does render later (react-query's
    // refetchOnWindowFocus, websocket file-status updates, ordinary hover and
    // selection state), so a user who is present sees the tree come back. A
    // tab nobody is looking at stays stale until it is looked at, which is
    // fine and is why there is no setTimeout to unwind on unmount.
    if (isStale && this.mayRetryAfterFailure(datum)) {
      // Nothing awaits this — contents() is called from render and returns
      // synchronously — so the rejection has to be handled here or it becomes
      // an unhandled rejection. That is what the CI log's endless
      // `PromiseRejectionEvent { isTrusted: true }` was. The failure is
      // already recorded on the tree and announced through onRebuild; report
      // it once for a human and let the render path carry on. Callers that
      // want to handle the error await load() directly.
      this.load(datum, loader).catch((error) => {
        console.error("Filetree load failed", datum.path, error);
      });
    }
    if (datum.children !== undefined) {
      return { type: isStale ? "stale" : "fresh", children: datum.children };
    }
    return { type: "loading" };
  }

  /**
   * False only while a just-failed load is still inside its back-off window.
   *
   * Wall clock rather than performance.now(): the monotonic one is not in
   * vitest's default `toFake` set, so it would cost fake-timer wiring in every
   * test. A backward clock step just extends the window by the step, and the
   * failure mode of a too-long window is a bounded delay, so the tradeoff is
   * one-sided at this timescale.
   */
  private mayRetryAfterFailure(datum: Datum): boolean {
    const failedAt = this.failedAt.get(datum.id);
    return (
      failedAt === undefined || Date.now() - failedAt >= RETRY_AFTER_FAILURE_MS
    );
  }

  async load(base: Datum, loader: LoaderFn) {
    // Performance optimization: don't perform multiple fetches if we're reloading.
    if (this.isReloading(base)) return;
    this.reloadingIds.add(base.id);
    // Trigger re-render to show loading state (spinner)
    // Use queueMicrotask to defer the callback to avoid setState during render
    if (this.onRebuild) {
      queueMicrotask(() => this.onRebuild?.(base));
    }
    // Pushed updates up to here are already in what the server will read.
    const startedAtSeq = this.pushSeq;
    try {
      const entities = await loader(base);
      this.splice(base, entities);
      // The answer is older than anything pushed while it was out.
      for (const pushed of this.pushedDuringLoad) {
        if (pushed.seq > startedAtSeq) pushed.reapply();
      }
      this.loadedAt.set(base.id, new Date());
      this.failedAt.delete(base.id);
      this.onRebuild?.(base);
    } catch (error) {
      // Record the failure before re-throwing. Success was already recorded
      // (`loadedAt`) and the in-flight guard is released below either way, so
      // without this the datum reads as "never loaded, nobody loading it" and
      // the next render starts the whole thing again — see contents().
      this.failedAt.set(base.id, Date.now());
      this.onRebuild?.(base);
      throw error;
    } finally {
      this.reloadingIds.delete(base.id);
      // No load left that could predate them.
      if (this.reloadingIds.size === 0) this.pushedDuringLoad = [];
    }
  }

  /**
   * Ensure all folders in the given path exist, creating any missing ones.
   * Used when adding or moving files to paths that may not exist yet.
   */
  ensureFoldersExist(folderPath: string[], driveId: string | null): void {
    for (let i = 1; i <= folderPath.length; i++) {
      const partialPath = folderPath.slice(0, i);
      const folderKey = JSON.stringify(partialPath);
      const existingFolder = Array.from(this.lookup.values()).find(
        (d) => JSON.stringify(d.path) === folderKey && d.type === "folder"
      );
      if (!existingFolder) {
        const folderDatum = new Datum(partialPath, "folder", {
          type: "folder",
          documentDate: "",
          driveId,
        });
        this.datums.push(folderDatum);
      }
    }
  }

  /**
   * Update an existing file's payload by taskId.
   * If newPath is provided and differs from current path, moves the file.
   * Used for WebSocket status updates.
   * @returns true if file was found and updated, false otherwise
   */
  updateFileByTaskId(
    taskId: string,
    payloadUpdates: Partial<RenderableData>,
    newPath?: string[]
  ): boolean {
    const datum = this.datums.find(
      (d) =>
        d.type === "file" &&
        d.payload?.type === "file" &&
        d.payload.taskId === taskId
    );
    if (!datum) return false;

    // Update payload
    datum.payload = {
      ...datum.payload,
      ...payloadUpdates,
    } as RenderableData;

    // Move if path changed
    if (newPath && JSON.stringify(datum.path) !== JSON.stringify(newPath)) {
      const parentPath = newPath.slice(0, -1);
      const driveId =
        datum.payload?.type === "file" ? datum.payload.driveId : null;

      // Ensure parent folders exist
      if (parentPath.length > 0) {
        this.ensureFoldersExist(parentPath, driveId);
      }

      // Move the file
      this.moveDatum(datum, parentPath, newPath[newPath.length - 1]);
    } else {
      // Just rebuild lookup (no move needed)
      this.rebuildLookup();
      this.onRebuild?.(datum);
    }

    return true;
  }

  /**
   * Add a new file to the tree at the specified path.
   * Creates any missing parent folders along the path.
   * Used for adding files directly from WebSocket data.
   */
  addFile(path: string[], payload: RenderableData): void {
    // Ensure all parent folders exist
    const parentPath = path.slice(0, -1);
    if (parentPath.length > 0) {
      const driveId = payload.type === "file" ? payload.driveId : null;
      this.ensureFoldersExist(parentPath, driveId);
    }

    // Add the file
    const fileDatum = new Datum(path, "file", payload);
    this.datums.push(fileDatum);

    // Rebuild lookup and trigger update
    this.lookup = rebuild(this.datums, this.sortBy, this.sortDirection, {
      readOnlyDriveNames: this.readOnlyDriveNames,
      sftpDriveNames: this.sftpDriveNames,
      sharepointRootNames: this.sharepointRootNames,
      isAcrossDrivesMode: this.isAcrossDrivesMode,
    });
    this.onRebuild?.(fileDatum);
  }

  /** Move a datum to a new parent, updating paths of all descendants.
   *  Optionally rename the item by providing newName.
   *  Returns any datums removed during a folder merge (for rollback).
   */
  moveDatum(datum: Datum, newParentPath: string[], newName?: string): Datum[] {
    const itemName = newName ?? datum.name;
    if (!itemName) return [];

    const oldPathLen = datum.path.length;
    const newPath = [...newParentPath, itemName];

    // Check if a folder already exists at the target path (merge case)
    if (datum.type === "folder") {
      const targetPathKey = JSON.stringify(newPath);
      const existingFolder = this.datums.find(
        (d) =>
          d !== datum &&
          d.type === "folder" &&
          JSON.stringify(d.path) === targetPathKey
      );

      if (existingFolder) {
        // Merge: reparent source folder's descendants under existing folder
        const updateDescendants = (parent: Datum) => {
          for (const child of parent.children ?? []) {
            child.path = [...newPath, ...child.path.slice(oldPathLen)];
            if (child.type === "folder") {
              updateDescendants(child);
            }
          }
        };
        updateDescendants(datum);

        // Remove source folder from datums (children stay, now under existing folder)
        this.datums = this.datums.filter((d) => d !== datum);

        this.rebuildLookup();
        this.onRebuild?.(existingFolder);
        return [datum];
      }
    }

    // Normal case: no merge
    // Increment version if any folder IDs will change (folders use path-based IDs)
    if (datum.type === "folder") {
      this.version++;
    }

    // Update this datum's path
    datum.path = newPath;

    // Update descendants using children links (O(descendants) instead of O(all datums))
    if (datum.type === "folder" && datum.children) {
      const updateDescendants = (parent: Datum) => {
        for (const child of parent.children ?? []) {
          // Replace the old prefix with new path
          child.path = [...newPath, ...child.path.slice(oldPathLen)];
          if (child.type === "folder") {
            updateDescendants(child);
          }
        }
      };
      updateDescendants(datum);
    }

    this.rebuildLookup();
    this.onRebuild?.(datum);
    return [];
  }

  /** Rebuild lookup map and parent/child links. */
  rebuildLookup(): void {
    this.lookup = rebuild(this.datums, this.sortBy, this.sortDirection, {
      readOnlyDriveNames: this.readOnlyDriveNames,
      sftpDriveNames: this.sftpDriveNames,
      sharepointRootNames: this.sharepointRootNames,
      isAcrossDrivesMode: this.isAcrossDrivesMode,
    });
  }

  /** Optimistically remove datums by ID (and all their descendants). */
  removeDatums(ids: Set<string>): void {
    this.datums = this.datums.filter((d) => !ids.has(d.id));
    this.lookup = rebuild(this.datums, this.sortBy, this.sortDirection, {
      readOnlyDriveNames: this.readOnlyDriveNames,
      sftpDriveNames: this.sftpDriveNames,
      sharepointRootNames: this.sharepointRootNames,
      isAcrossDrivesMode: this.isAcrossDrivesMode,
    });
    this.onRebuild?.(this.root);
  }

  /** Mark a datum as needing reload on the next render cycle. */
  invalidate(datum: Datum): void {
    this.loadedAt.delete(datum.id);
    // An explicit refresh is a fresh request, not another turn of the render
    // loop contents() backs off from, so it skips the back-off window (#11724).
    this.failedAt.delete(datum.id);
  }

  /** Update sort settings and rebuild (no-op if unchanged). */
  setSort(sortBy: "name" | "date", sortDirection: "asc" | "desc"): void {
    if (this.sortBy === sortBy && this.sortDirection === sortDirection) return;
    this.sortBy = sortBy;
    this.sortDirection = sortDirection;
    this.rebuildLookup();
    this.onRebuild?.(this.root);
  }

  /** Replaces descendants of `base` with `children`. Pure data replacement. */
  splice(base: Datum, children: LoadedEntity[]) {
    const addedPaths = new Set<string>();
    const result: Datum[] = [];
    const removed: string[] = [];

    // Keep datums not under base's path (and base itself)
    for (const d of this.datums) {
      const pathKey = JSON.stringify(d.path);
      const isDescendant =
        d.path.length > base.path.length && arrayIsPrefixOf(base.path, d.path);

      if (!isDescendant) {
        result.push(d);
        addedPaths.add(pathKey);
      } else {
        removed.push(pathKey);
        // This descendant is being replaced by fresh data, so whatever its own
        // last load did is no longer the current answer. Without this a child
        // that failed keeps its back-off after the parent reloaded fine, and
        // an explicit invalidate() of the parent would not clear it (#11724).
        this.failedAt.delete(d.id);
      }
    }

    // Add new children
    const added: string[] = [];
    for (const child of children) {
      const childKey = JSON.stringify(child.path);
      if (addedPaths.has(childKey)) continue;
      if (!arrayIsPrefixOf(base.path, child.path)) continue;
      addedPaths.add(childKey);
      added.push(childKey);
      result.push(new Datum(child.path, child.type, child.payload));
    }

    this.datums = result;
    this.lookup = rebuild(this.datums, this.sortBy, this.sortDirection, {
      readOnlyDriveNames: this.readOnlyDriveNames,
      sftpDriveNames: this.sftpDriveNames,
      sharepointRootNames: this.sharepointRootNames,
      isAcrossDrivesMode: this.isAcrossDrivesMode,
    });
  }
}

/** Rebuilds parent/children links from flat datum list */
function rebuild(
  source: Datum[],
  sortBy: "name" | "date" = "name",
  sortDirection: "asc" | "desc" = "asc",
  ctx?: ReadOnlyContext
) {
  // Reset scratch state
  for (const datum of source) {
    datum.parent = undefined;
    datum.children = undefined;
  }

  // Build lookups
  const lookup = new Map<string, Datum>();
  const byPath = new Map<string, Datum[]>();
  for (const datum of source) {
    lookup.set(datum.id, datum);
    const key = JSON.stringify(datum.path);
    const list = byPath.get(key) || [];
    list.push(datum);
    byPath.set(key, list);
  }

  // Link parents and children
  for (const datum of source) {
    if (datum.path.length === 0) continue;
    const parentPath = datum.path.slice(0, -1);
    const parentKey = JSON.stringify(parentPath);
    const parent = byPath.get(parentKey)?.find((d) => d.type === "folder");
    if (parent) {
      datum.parent = parent;
      (parent.children ||= []).push(datum);
    }
  }

  sortFileTree(source, sortBy, sortDirection, ctx);
  return lookup;
}

export class Datum {
  constructor(
    public path: string[],
    public type: "file" | "folder",
    public payload?: DatumPayload
  ) {}

  /** Stable ID derived from immutable properties */
  get id(): string {
    if (this.path.length === 0) return "root";

    // For files with taskId, use taskId (immutable, so safe to compute)
    if (
      this.type === "file" &&
      this.payload?.type === "file" &&
      this.payload.taskId
    ) {
      return `file:${this.payload.taskId}`;
    }

    // For folders and files without taskId, use path + driveId
    const driveId = this.payload?.driveId;
    return driveId
      ? JSON.stringify([driveId, ...this.path])
      : JSON.stringify(this.path);
  }

  get name(): string | undefined {
    if (this.path.length === 0) return undefined;
    return this.path[this.path.length - 1];
  }

  // Scratch state (rebuilt on every change)
  parent: Datum | undefined = undefined;
  children: undefined | Datum[] = undefined;
}

export interface LoadedEntity {
  path: string[];
  type: "file" | "folder";
  payload?: DatumPayload;
}

export type LoaderFn = (datum: Datum) => Promise<LoadedEntity[]>;

type DatumContents =
  | { type: "loading" }
  | { type: "stale" | "fresh"; children: Datum[] };

function arrayIsPrefixOf<T>(prefix: T[], bigger: T[]): boolean {
  if (bigger.length < prefix.length) return false;
  for (let i = 0; i < prefix.length; i++) {
    if (prefix[i] !== bigger[i]) return false;
  }
  return true;
}
